/**
 * Sign-off / Save / Save-as-template modal — Phase 6++.
 *
 * Unified save entry point for the RA Detail page. Captures a signer name +
 * role and POSTs to /sign-off; also surfaces:
 *   - Save only (when isDirty + onSave) — persist edits, skip sign-off
 *   - Save & sign & lock (default submit when dirty) — save then sign off
 *   - Sign & lock (default submit when clean) — just sign off
 *   - Save as template / Save & save as template — opens a collapsible panel
 *     with a template name + workspace/dept destination checkboxes; on submit
 *     calls onSaveAsTemplate(name, { toWorkspace, toDept }) which the parent
 *     wraps over /api/risk-assessments/:id/create-template.
 *
 * Defaults the signer name + role from the authenticated user / active dept
 * role for one-click signing in the common case.
 */
import React, { useEffect, useState } from 'react';
import { apiPost } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useDepartment } from '../contexts/DepartmentContext';
import { BTN } from '../constants/buttons';
import ErrorMessage from './ErrorMessage';
import { FIELD_LABEL_CLASS } from '../constants/inputs';
import { MODAL_OVERLAY } from '../constants/overlayLayers';

export default function RiskAssessmentSignOffModal({
  raId,
  isFirstSignature,
  isDirty = false,
  onSave = null,
  onClose,
  onSigned,
  // Save-as-template integration (all optional — when none are provided the
  // template section is hidden and the modal behaves exactly as before).
  canSaveToWorkspace = false,
  canSaveToDept = false,
  myPersonalProductionId = null,
  defaultTemplateName = '',
  onSaveAsTemplate = null,
}) {
  const { user } = useAuth();
  const { activeRole, isAdmin, isBuyer } = useDepartment();
  const [signerName, setSignerName] = useState(user?.display_name || '');
  const [signerRole, setSignerRole] = useState(() => {
    if (isAdmin) return 'Admin';
    if (isBuyer) return 'Buyer';
    if (activeRole) return activeRole.split('_').map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');
    return '';
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Template panel state — collapsible. Hidden entirely when the user has no
  // template-save permissions at all, or the parent didn't wire onSaveAsTemplate.
  const canShowTemplatePanel = !!onSaveAsTemplate && (canSaveToWorkspace || canSaveToDept);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState(defaultTemplateName || '');
  // Default destination ticks: dept for curators (preserves the prior modal's
  // behaviour); workspace for regular users (their only option).
  const [tplTargetWorkspace, setTplTargetWorkspace] = useState(!canSaveToDept && canSaveToWorkspace);
  const [tplTargetDept, setTplTargetDept] = useState(canSaveToDept);
  const [templateError, setTemplateError] = useState(null);

  useEffect(() => { setSignerName(user?.display_name || ''); }, [user]);
  useEffect(() => { setTemplateName(defaultTemplateName || ''); }, [defaultTemplateName]);

  // Whether the template panel is "active" — visible AND filled in well
  // enough to enable the Save-as-template action button.
  const templateReady = templateOpen
    && templateName.trim().length > 0
    && (
      (tplTargetWorkspace && canSaveToWorkspace && !!myPersonalProductionId)
      || (tplTargetDept && canSaveToDept)
    );

  // Run the parent's save callback. Returns true on success. The parent's
  // handleSaveAll signals failure with a `false` return; we surface it in the
  // modal so the user can fix the issue without losing the modal state.
  const runSave = async () => {
    if (!onSave) return true;
    const ok = await onSave();
    if (!ok) {
      setError('Save failed — fix the issue and try again, or Cancel to discard.');
      return false;
    }
    return true;
  };

  // Submit the sign-off POST. If isDirty + onSave was passed in, expect the
  // caller to have already run save (via handleSaveAndSign below); we don't
  // double-save here to avoid the second PUT racing the first.
  const signOff = async () => {
    if (!signerName.trim()) {
      setError('Signer name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await apiPost(`/api/risk-assessments/${raId}/sign-off`, {
        signer_name: signerName.trim(),
        signer_role: signerRole.trim(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || 'Failed to sign');
      }
      const data = await res.json();
      onSigned(data);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  // Run the parent's save-as-template callback. Returns true on success;
  // surfaces template-panel-local validation + server errors otherwise.
  const runSaveAsTemplate = async () => {
    if (!onSaveAsTemplate) return false;
    setTemplateError(null);
    try {
      const ok = await onSaveAsTemplate(templateName.trim(), {
        toWorkspace: !!tplTargetWorkspace,
        toDept: !!tplTargetDept,
      });
      return !!ok;
    } catch (err) {
      setTemplateError(err.message || 'Failed to save template');
      return false;
    }
  };

  const handleSaveOnly = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    const ok = await runSave();
    setSaving(false);
    if (ok) onClose();
  };

  const handleSaveAndSign = async () => {
    if (saving) return;
    if (!signerName.trim()) {
      setError('Signer name is required');
      return;
    }
    setSaving(true);
    setError(null);
    const ok = await runSave();
    if (!ok) {
      setSaving(false);
      return;
    }
    // Save succeeded — proceed straight into sign-off. signOff() manages its
    // own saving flag, so reset ours so it can transition correctly.
    setSaving(false);
    await signOff();
  };

  const handleSaveAndTemplate = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    setTemplateError(null);
    if (isDirty && onSave) {
      const ok = await runSave();
      if (!ok) {
        setSaving(false);
        return;
      }
    }
    const tplOk = await runSaveAsTemplate();
    setSaving(false);
    if (tplOk) onClose();
  };

  // The primary submit (Enter / clicking the green button) — picks the most
  // appropriate "happy path" given current state. Save-as-template never
  // becomes the default submit (must be explicit click) because it's a less
  // common action and the green button stays focused on sign-off.
  const submit = (e) => {
    e?.preventDefault?.();
    if (isDirty && onSave) {
      handleSaveAndSign();
    } else {
      signOff();
    }
  };

  return (
    <div
      className={`fixed inset-0 z-40 bg-black/50 flex items-start justify-center p-4 pt-[8vh] overflow-y-auto ${MODAL_OVERLAY}`}
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-5"
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
          {isFirstSignature ? 'Sign off this risk assessment' : 'Add another signature'}
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          {isFirstSignature
            ? 'Once signed, the RA becomes read-only. Use "Revise" to make any further changes.'
            : 'Multiple signers can sign off the same RA — appends another signature.'}
        </p>

        {isDirty && (
          <div
            className="mb-4 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-xs px-3 py-2"
            data-testid="ra-signoff-dirty-banner"
          >
            You have unsaved changes. Pick <strong>Save only</strong> to persist them, or <strong>Save &amp; sign &amp; lock</strong> to save and sign off in one step.
          </div>
        )}

        {error && <ErrorMessage error={error} />}

        <label className="block mb-3">
          <span className={FIELD_LABEL_CLASS}>Signer name *</span>
          <input
            type="text"
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
            className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-base md:text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            autoFocus
            data-testid="ra-signoff-name"
          />
        </label>
        <label className="block mb-4">
          <span className={FIELD_LABEL_CLASS}>Signer role</span>
          <input
            type="text"
            value={signerRole}
            onChange={(e) => setSignerRole(e.target.value)}
            placeholder="e.g. SFX Supervisor, Safety Officer"
            className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-base md:text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            data-testid="ra-signoff-role"
          />
        </label>

        {canShowTemplatePanel && (
          <div className="mb-4 rounded-md border border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => setTemplateOpen((o) => !o)}
              aria-expanded={templateOpen}
              className="w-full flex items-center justify-between px-3 py-2 text-left bg-gray-50 dark:bg-slate-700/30 hover:bg-gray-100 dark:hover:bg-slate-700/50 transition-colors rounded-md"
              data-testid="ra-signoff-template-toggle"
            >
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">Save as template</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform ${templateOpen ? '' : '-rotate-90'}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {/* Grid-template-rows trick: animating between 0fr and 1fr gives
                a smooth height transition without measuring children. The
                inner div clips overflow during the transition. Border-t only
                while open so the rule doesn't peek when collapsed. */}
            <div
              className={`grid transition-[grid-template-rows] duration-300 ease-out ${templateOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
              aria-hidden={!templateOpen}
            >
              <div className="overflow-hidden">
                <div className={`px-3 py-3 ${templateOpen ? 'border-t border-gray-200 dark:border-gray-700' : ''}`}>
                  {templateError && <ErrorMessage error={templateError} />}
                  <label className="block mb-3">
                    <span className={FIELD_LABEL_CLASS}>Template name *</span>
                    <input
                      type="text"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      maxLength={200}
                      tabIndex={templateOpen ? 0 : -1}
                      className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-base md:text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      data-testid="ra-signoff-template-name"
                    />
                  </label>
                  <fieldset className="block mb-2">
                    <legend className={FIELD_LABEL_CLASS}>Save to *</legend>
                    <div className="space-y-1.5">
                      {canSaveToDept && (
                        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={tplTargetDept}
                            onChange={(e) => setTplTargetDept(e.target.checked)}
                            tabIndex={templateOpen ? 0 : -1}
                            className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                            data-testid="ra-signoff-template-target-dept"
                          />
                          Department library (visible to your dept)
                        </label>
                      )}
                      {canSaveToWorkspace && (
                        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={tplTargetWorkspace}
                            onChange={(e) => setTplTargetWorkspace(e.target.checked)}
                            disabled={!myPersonalProductionId}
                            tabIndex={templateOpen ? 0 : -1}
                            className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                            data-testid="ra-signoff-template-target-workspace"
                          />
                          My workspace (visible only to you)
                        </label>
                      )}
                    </div>
                  </fieldset>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    Created alongside whichever Save action you choose below.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 flex-wrap">
          <button type="button" onClick={onClose} className={BTN.secondary} disabled={saving}>
            Cancel
          </button>
          {isDirty && onSave && !templateReady && (
            <button
              type="button"
              onClick={handleSaveOnly}
              className={BTN.primary}
              disabled={saving}
              data-testid="ra-signoff-save-only"
            >
              {saving ? 'Saving…' : 'Save only'}
            </button>
          )}
          {templateReady && (
            <button
              type="button"
              onClick={handleSaveAndTemplate}
              className={BTN.primary}
              disabled={saving}
              data-testid="ra-signoff-save-template"
            >
              {saving
                ? (isDirty && onSave ? 'Saving & saving template…' : 'Saving template…')
                : (isDirty && onSave ? 'Save & save as template' : 'Save as template')}
            </button>
          )}
          <button
            type="submit"
            className={BTN.confirm}
            disabled={saving || !signerName.trim()}
            data-testid="ra-signoff-submit"
          >
            {saving
              ? (isDirty && onSave ? 'Saving & signing…' : 'Signing…')
              : isDirty && onSave
                ? (isFirstSignature ? 'Save & sign & lock' : 'Save & add signature')
                : (isFirstSignature ? 'Sign & lock' : 'Add signature')}
          </button>
        </div>
      </form>
    </div>
  );
}
